import React from 'react';
import './App.css';
import UniversalHeader from './UniversalHeader';
import { useNavigate } from 'react-router-dom';
import { supabase } from './supabaseClient';
import MaintenanceAssignStaffModal from './MaintenanceAssignStaffModal';

function displayProperty(row) {
  const rel = row.properties;
  const p = Array.isArray(rel) ? rel[0] : rel;
  if (p?.name) return p.location ? `${p.name} (${p.location})` : p.name;
  return row.property_name?.trim() || '—';
}

function tenantFromRow(row) {
  const rel = row.prospective_tenants;
  const t = Array.isArray(rel) ? rel[0] : rel;
  return t;
}

function staffFromRequest(row) {
  const rel = row.maintenance_staff;
  return Array.isArray(rel) ? rel[0] : rel;
}

function assigneeDisplay(row) {
  const s = staffFromRequest(row);
  if (s?.full_name) {
    const name = (s.full_name || '').trim();
    const role = (s.role || '').trim();
    return role ? `${name} (${role})` : name;
  }
  return (row.assignee || '').trim() || '—';
}

function mapRow(row) {
  const linked = tenantFromRow(row);
  return {
    id: row.id,
    tenant: row.tenant_name || linked?.full_name || '—',
    email: linked?.email || row.contact_info || '—',
    property: displayProperty(row),
    room: row.room_label || '—',
    issue: row.issue_description || '',
    status: row.status || 'pending',
    date: row.logged_on || row.created_at,
    assignee: assigneeDisplay(row),
    assignedStaffId: row.assigned_staff_id || null,
  };
}

function initials(name) {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] || ''}${parts[parts.length - 1][0] || ''}`.toUpperCase();
}

const statusLabel = {
  pending: 'Pending',
  in_progress: 'In progress',
  completed: 'Completed',
};

/**
 * Admin list: only maintenance rows created from /tenant/maintenance
 * (prospective_tenant_id is set). Requires sql/add_tenant_maintenance_portal_supabase.sql.
 */
function TenantMaintenanceRequests() {
  const navigate = useNavigate();
  const [search, setSearch] = React.useState('');
  const [statusFilter, setStatusFilter] = React.useState('all');
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [listError, setListError] = React.useState('');

  const [assignTarget, setAssignTarget] = React.useState(null);
  const [staffList, setStaffList] = React.useState([]);
  const [staffLoading, setStaffLoading] = React.useState(false);
  const [assignSaving, setAssignSaving] = React.useState(false);

  const load = React.useCallback(async () => {
    if (!supabase) {
      setListError('Supabase is not configured.');
      setRows([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setListError('');
    const { data, error } = await supabase
      .from('maintenance_requests')
      .select(
        `
        id,
        tenant_name,
        contact_info,
        property_name,
        room_label,
        issue_description,
        status,
        logged_on,
        created_at,
        assignee,
        assigned_staff_id,
        prospective_tenant_id,
        properties (
          name,
          location
        ),
        prospective_tenants (
          full_name,
          email
        ),
        maintenance_staff (
          id,
          full_name,
          role
        )
      `
      )
      .not('prospective_tenant_id', 'is', null)
      .order('created_at', { ascending: false });

    if (error) {
      setListError(
        `${error.message} — If the column is missing, run sql/add_tenant_maintenance_portal_supabase.sql in Supabase.`
      );
      setRows([]);
    } else {
      setRows((data || []).map(mapRow));
    }
    setLoading(false);
  }, []);

  React.useEffect(() => {
    load();
  }, [load]);

  const loadStaffForAssign = React.useCallback(async () => {
    if (!supabase) return;
    setStaffLoading(true);
    const { data, error } = await supabase
      .from('maintenance_staff')
      .select('id, full_name, role, email')
      .order('full_name', { ascending: true });
    if (!error && data) setStaffList(data);
    else setStaffList([]);
    setStaffLoading(false);
  }, []);

  const handleAssign = (r) => {
    setAssignTarget(r);
    if (staffList.length === 0) loadStaffForAssign();
  };

  const closeAssignModal = () => setAssignTarget(null);

  const confirmAssignStaff = async (staffId) => {
    if (!assignTarget || !supabase || !staffId) return;
    const member = staffList.find((s) => s.id === staffId);
    const name = (member?.full_name || '').trim() || 'Staff';
    const role = (member?.role || '').trim();
    const assigneeText = role ? `${name} (${role})` : name;
    setAssignSaving(true);
    try {
      const { error } = await supabase
        .from('maintenance_requests')
        .update({
          assigned_staff_id: staffId,
          assignee: assigneeText,
        })
        .eq('id', assignTarget.id);
      if (error) throw error;
      closeAssignModal();
      await load();
    } catch (err) {
      // eslint-disable-next-line no-alert
      alert(
        err?.message ||
          'Could not assign. Run sql/add_maintenance_assigned_staff_supabase.sql in Supabase (adds assigned_staff_id).'
      );
    } finally {
      setAssignSaving(false);
    }
  };

  React.useEffect(() => {
    if (!assignTarget) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setAssignTarget(null);
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [assignTarget]);

  const filtered = React.useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (statusFilter !== 'all' && r.status !== statusFilter) return false;
      if (!q) return true;
      const hay = `${r.tenant} ${r.email} ${r.property} ${r.room} ${r.issue}`.toLowerCase();
      return hay.includes(q);
    });
  }, [rows, search, statusFilter]);

  return (
    <div className="maint-root">
      <UniversalHeader />

      <div className="maint-shell">
        <header className="maint-header">
          <div>
            <h1 className="maint-title">Maintenance requests</h1>
            <p className="maint-subtitle">
              Issues submitted by tenants from the tenant portal (<strong>/tenant/maintenance</strong>). For all
              work orders including staff-created ones, use{' '}
              <button type="button" className="maint-inline-link" onClick={() => navigate('/maintenance')}>
                Maintenance
              </button>
              .
            </p>
          </div>

          <div className="maint-actions">
            <button type="button" className="btn-ghost maint-btn" onClick={load} disabled={loading}>
              Refresh
            </button>
          </div>
        </header>

        {listError ? (
          <p className="properties-save-error" role="alert">
            {listError}
          </p>
        ) : null}

        <section className="maint-toolbar">
          <div className="maint-search">
            <div className="input-shell maint-search-shell">
              <div className="input-icon">🔍</div>
              <input
                className="input-element"
                type="text"
                placeholder="Search tenant, email, property, or issue"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>

          <div className="maint-filters">
            <div className="maint-status-chips">
              {['all', 'pending', 'in_progress', 'completed'].map((key) => (
                <button
                  key={key}
                  type="button"
                  className={`maint-status-chip ${statusFilter === key ? 'maint-status-chip--active' : ''}`}
                  onClick={() => setStatusFilter(key)}
                >
                  {key === 'all' ? 'All status' : statusLabel[key]}
                </button>
              ))}
            </div>
          </div>
        </section>

        <main className="maint-layout">
          <section className="maint-list">
            <div className="maint-list-header">
              <span className="maint-list-label">Tenant portal requests</span>
              <span className="maint-list-count">{loading ? '…' : `${filtered.length} shown`}</span>
            </div>

            {loading ? (
              <p className="properties-list-empty">Loading…</p>
            ) : filtered.length === 0 ? (
              <p className="properties-list-empty">
                {rows.length === 0
                  ? 'No tenant maintenance requests yet. Tenants submit from their portal after you run sql/add_tenant_maintenance_portal_supabase.sql.'
                  : 'No requests match your search or filters.'}
              </p>
            ) : (
              <div className="maint-grid">
                {filtered.map((r) => (
                  <article className="maint-card" key={r.id}>
                    <div className="maint-card-main">
                      <div className="maint-avatar">
                        <span>{initials(r.tenant)}</span>
                      </div>
                      <div className="maint-main-text">
                        <h2 className="maint-card-tenant">{r.tenant}</h2>
                        <p className="maint-card-property">
                          {r.property} · Room {r.room}
                        </p>
                        <p className="maint-card-issue">{r.issue}</p>
                        <p className="tenant-maintenance-req-email">{r.email}</p>
                      </div>
                      <span className={`maint-status-badge maint-status-badge--${r.status}`}>
                        {statusLabel[r.status]}
                      </span>
                    </div>

                    <div className="maint-card-meta">
                      <div className="maint-meta-block">
                        <span className="maint-meta-label">Logged</span>
                        <span className="maint-meta-value">
                          {r.date ? new Date(r.date).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : '—'}
                        </span>
                      </div>
                      <div className="maint-meta-block">
                        <span className="maint-meta-label">Assigned</span>
                        <span className="maint-meta-value">{r.assignee}</span>
                      </div>
                    </div>

                    <div className="maint-card-icon-row">
                      <button
                        type="button"
                        className="properties-icon-btn"
                        aria-label="Edit request"
                        onClick={() => navigate(`/maintenance/${r.id}/edit`)}
                      >
                        ✏️
                      </button>
                    </div>

                    {!r.assignedStaffId ? (
                      <div className="maint-card-actions">
                        <button
                          type="button"
                          className="btn-ghost maint-action-btn"
                          onClick={() => handleAssign(r)}
                        >
                          Assign task
                        </button>
                      </div>
                    ) : null}
                  </article>
                ))}
              </div>
            )}
          </section>
        </main>
      </div>

      <MaintenanceAssignStaffModal
        open={Boolean(assignTarget)}
        title="Assign task to staff"
        subtitle={
          assignTarget
            ? `${assignTarget.tenant} · ${(assignTarget.issue || '').slice(0, 100)}${
                (assignTarget.issue || '').length > 100 ? '…' : ''
              }`
            : ''
        }
        staff={staffList}
        staffLoading={staffLoading}
        saving={assignSaving}
        onClose={closeAssignModal}
        onConfirm={confirmAssignStaff}
      />
    </div>
  );
}

export default TenantMaintenanceRequests;
